import type { DeskFileChange, DeskFileCommit, DeskWorkItemFiles, WorkItemDetail } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, ChevronRight, Download, FolderGit2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Confirm } from '@/components/confirm';
import { CommitAuthor, CommitSha } from '@/components/desk/desk-files';
import { TextDiff } from '@/components/files/text-diff';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { MetaBadge } from '@/components/status-badge';
import { ErrorState } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { errorMessage } from '@/lib/api';
import { CHANGE_KIND, deskFilesApi, downloadDeskFile, isTextFile, MAX_RESOLVE_ATTEMPTS, WORK_ITEM_FILES_STATUS } from '@/lib/desk-files';
import { useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { useToast } from '@/lib/toast';
import { cn } from '@/lib/utils';

/**
 * What the Work Item's runs changed in the desk's files (docs/features/desk-files.md): its own copy of them, merged
 * into the desk's files when the item completes. Where the desk changed the same lines meanwhile, its agent resolves
 * the conflict. Hidden while the item never changed a file.
 */
export function WorkItemFilesSection({ item }: { item: WorkItemDetail }) {
  const files = useQuery({ queryKey: qk.workItemFiles(item.id), queryFn: () => deskFilesApi.ofWorkItem(item.id) });
  if (files.isPending) return null;
  if (files.isError) return <ErrorState title={t('The file changes couldn’t be loaded')} error={files.error} onRetry={() => void files.refetch()} />;
  if (!files.data) return null;
  return <FilesSection item={item} files={files.data} />;
}

function statusText(files: DeskWorkItemFiles) {
  switch (files.status) {
    case 'open':
      return t('Its own copy of the desk’s files; goes into the desk when the Work Item is complete.');
    case 'merging':
      return t('The Work Item is complete; its changes are going into the desk’s files.');
    case 'conflict':
      return files.attempts > MAX_RESOLVE_ATTEMPTS
        ? t('Collides with changes on the desk in: {paths}. Its agent couldn’t resolve them in {n} tries — ask for the change again, or discard it.', {
            paths: files.conflicts.join(', '),
            n: MAX_RESOLVE_ATTEMPTS,
          })
        : t('Collides with changes on the desk in: {paths}; its agent resolves them (try {n}).', { paths: files.conflicts.join(', '), n: Math.max(files.attempts, 1) });
    case 'merged':
      return t('In the desk’s files.');
    case 'discarded':
      return t('These changes were discarded; the desk’s files stayed as they were.');
  }
}

function FilesSection({ item, files }: { item: WorkItemDetail; files: DeskWorkItemFiles }) {
  // Whether the person may change the desk's files: the desk's tree says (the Files tab shares the query).
  const tree = useQuery({ queryKey: qk.deskFileTree(item.deskId), queryFn: () => deskFilesApi.tree(item.deskId) });
  const canWrite = tree.data?.canWrite !== false;
  const discard = useApiMutation(() => deskFilesApi.discard(item.id), {
    invalidate: [qk.workItemFiles(item.id), qk.deskFiles(item.deskId), qk.workItemActivity(item.id)],
    errorTitle: t('Not discarded'),
    success: t('The file changes were discarded'),
  });
  const live = files.status === 'open' || files.status === 'conflict';
  const conflicts = new Set(files.conflicts);

  return (
    <Section
      icon={<FolderGit2 />}
      title={
        <span className="inline-flex flex-wrap items-center gap-2">
          {t('Files')}
          <MetaBadge meta={WORK_ITEM_FILES_STATUS[files.status]} />
        </span>
      }
      description={
        <>
          {statusText(files)}
          {files.status === 'merged' && files.mergeCommit && (
            <>
              {' '}
              <CommitSha sha={files.mergeCommit} />
            </>
          )}
        </>
      }
      actions={
        <>
          <Button asChild variant="ghost" size="xs">
            <Link to="/w/$workspaceId/desks/$deskId" params={{ workspaceId: item.workspaceId, deskId: item.deskId }} search={{ tab: 'files' }}>
              {t('Desk files')}
            </Link>
          </Button>
          {live && canWrite && (
            <Confirm
              title={t('Discard the file changes?')}
              description={t('The desk’s files stay as they are; what the agents changed for this Work Item is dropped. The Work Item itself goes on.')}
              confirmLabel={t('Discard')}
              destructive
              onConfirm={() => discard.mutateAsync()}
            >
              <Button variant="ghost" size="xs" className="text-destructive hover:text-destructive">
                <Trash2 /> {t('Discard changes')}
              </Button>
            </Confirm>
          )}
        </>
      }
    >
      {files.changes.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('No changes against the desk’s files.')}</p>
      ) : (
        <ul className="-mx-5 divide-y border-y">
          {files.changes.map((c) => (
            <ChangeRow key={c.path} deskId={item.deskId} change={c} conflict={conflicts.has(c.path)} />
          ))}
        </ul>
      )}
      {files.commits.length > 0 && <RunCommits commits={files.commits} />}
    </Section>
  );
}

/** One path's change against the desk's files, with what changed in a text. */
function ChangeRow({ deskId, change: c, conflict }: { deskId: string; change: DeskFileChange; conflict: boolean }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const text = isTextFile(c.path, null);
  return (
    <li className="px-5 py-2.5">
      <div className="flex min-w-0 items-center gap-2">
        <Badge tone={CHANGE_KIND[c.status].tone} className="w-[4.75rem] justify-center px-1.5 py-0 text-[11px]">
          {CHANGE_KIND[c.status].label}
        </Badge>
        {text ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="flex min-w-0 flex-1 items-center gap-1 text-left font-mono text-[13px] hover:text-primary"
            title={open ? t('Hide what changed') : t('Show what changed')}
          >
            <ChevronRight className={cn('size-3.5 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')} />
            <span className="truncate">{c.path}</span>
          </button>
        ) : (
          <span className="min-w-0 flex-1 truncate font-mono text-[13px]" title={c.path}>
            {c.path}
          </span>
        )}
        {conflict && (
          <Badge tone="amber" title={t('Collides with a change on the desk; its agent resolves it')}>
            <AlertTriangle /> {t('Conflict')}
          </Badge>
        )}
        {c.after && (
          <Button
            variant="ghost"
            size="icon-sm"
            className="-my-1 shrink-0"
            title={t('Download')}
            aria-label={t('Download {name}', { name: c.path })}
            onClick={() => downloadDeskFile(deskId, c.after!, c.path).catch((err) => toast.error(t('Download failed'), errorMessage(err)))}
          >
            <Download />
          </Button>
        )}
      </div>
      {open && <TextDiff deskId={deskId} before={c.before ? { sha: c.before } : null} after={c.after ? { sha: c.after } : null} className="mt-2" />}
    </li>
  );
}

/** What each run saved (one commit per run), oldest first — the steps behind the changes above. */
function RunCommits({ commits }: { commits: DeskFileCommit[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        <ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} />
        {commits.length === 1 ? t('1 save by the agents') : t('{n} saves by the agents', { n: commits.length })}
      </button>
      {open && (
        <ol className="mt-2 flex flex-col gap-2 border-l pl-3">
          {commits.map((c) => (
            <li key={c.sha} className="text-xs">
              <div className="flex min-w-0 items-start gap-2">
                <CommitSha sha={c.sha} className="mt-0.5" />
                <span className="min-w-0 text-sm break-words">{c.message || (c.changes.length === 1 ? t('1 file changed') : t('{n} files changed', { n: c.changes.length }))}</span>
              </div>
              <div className="mt-0.5 text-muted-foreground">
                <CommitAuthor author={c.author} /> · <RelativeTime value={c.at} />
                {c.changes.length > 0 && ' · '}
                {c.changes
                  .slice(0, 4)
                  .map((x) => `${CHANGE_KIND[x.status].label} ${x.path}`)
                  .join(', ')}
                {c.changes.length > 4 && ` ${t('and {n} more', { n: c.changes.length - 4 })}`}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
